import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { BASE_URL } from '../config/config';

const ContentViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { content, type, title } = location.state || {};
  const [isFullscreen, setIsFullscreen] = useState(false);

  const API_BASE_URL = BASE_URL;

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return url; // Return as-is if no pattern matches
  };

  // Download file
  const handleDownload = () => {
    const fileUrl = `${API_BASE_URL}/${content}`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = content.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Render video content
  const renderVideo = () => {
    const videoId = getYouTubeVideoId(content);
    
    return (
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        {/* Mobile ONLY iframe (< 768px) */}
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-lg block md:hidden"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1&playsinline=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={false}
        />
        
        {/* Desktop/Tablet iframe (>= 768px) - Hidden on mobile */}
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-lg hidden md:block"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen={true}
        />
        
        {/* Overlays to prevent YouTube redirects on all platforms */}
        
        {/* Mobile Overlays - Hidden on desktop (< 768px) */}
        <div className="md:hidden">
          {/* Hide YouTube logo (top-left corner) */}
          <div 
            className="absolute top-0 left-0 w-40 h-12 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'transparent',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          />
          
          {/* Hide title/info area (top area) */}
          {/* <div 
            className="absolute top-0 left-14 right-0 h-8 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'green',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          /> */}
          
          {/* Hide share/watch on YouTube button (bottom-right corner) */}
          <div 
            className="absolute bottom-0 right-0 w-full h-10 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'transparent',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          />
          
          {/* Optional: Hide bottom-left controls */}
         
        </div>
        
        {/* Desktop Overlays - Hidden on mobile (>= 768px) */}
        <div className="hidden md:block">
          {/* Hide YouTube logo (top-left corner) */}
          <div 
            className="absolute top-0 left-0 w-28 h-16 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'transparent',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          />
          
          {/* Hide title/info area (top area) */}
          <div 
            className="absolute top-0 left-28 right-0 h-16 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'transparent',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          />
          
          {/* Hide share/watch on YouTube button (bottom-right corner) */}
          <div 
            className="absolute bottom-0 right-0 w-full h-16 z-10"
            style={{ 
              pointerEvents: 'auto',
              background: 'transparent',
              cursor: 'default'
            }}
            onClick={(e) => e.preventDefault()}
          />
        </div>
      </div>
    );
  };

  // Render PDF content
  const renderPDF = () => {
    const fileUrl = `${API_BASE_URL}/${content}`;
    
    return (
      <div className="w-full h-full">
        <iframe
          src={`${fileUrl}#toolbar=1&navpanes=1&scrollbar=1`}
          className="w-full rounded-lg"
          style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}
          title={title}
        />
      </div>
    );
  };

  // Render document (as PDF)
  const renderDocument = () => {
    const fileUrl = `${API_BASE_URL}/${content}`;
    const fileExtension = content.split('.').pop().toLowerCase();
    
    // If it's a PDF, use PDF viewer
    if (fileExtension === 'pdf') {
      return renderPDF();
    }
    
    // For other document types, try Google Docs Viewer
    return (
      <div className="w-full h-full">
        <iframe
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
          className="w-full rounded-lg"
          style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}
          title={title}
        />
      </div>
    );
  };

  // Render appropriate content based on type
  const renderContent = () => {
    switch (type) {
      case 'video':
        return renderVideo();
      case 'pdf':
        return renderPDF();
      case 'document':
        return renderDocument();
      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-600">Content type not supported</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg md:text-xl font-bold text-gray-800 truncate">
                  {title || 'Content Viewer'}
                </h1>
                <p className="text-sm text-gray-600 capitalize">
                  {type === 'video' ? 'Video Content' : type === 'pdf' ? 'PDF Document' : 'Document'}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-4">
              {(type === 'pdf' || type === 'document') && (
                <button
                  onClick={handleDownload}
                  className="p-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                  title="Download"
                >
                  <FiDownload className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? (
                  <FiMinimize2 className="w-5 h-5" />
                ) : (
                  <FiMaximize2 className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          {content ? (
            renderContent()
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiArrowLeft className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No Content Available</h3>
              <p className="text-gray-600 mb-6">Unable to load the content</p>
              <button onClick={() => navigate(-1)} className="btn-secondary">
                Go Back
              </button>
            </div>
          )}
        </div>

        {/* Additional Info */}
        {content && type === 'video' && (
          <div className="mt-6 card">
            <h3 className="text-lg font-bold text-gray-800 mb-2">About this video</h3>
            <p className="text-gray-600">{title}</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ContentViewer;

